import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next'; // unused, but keep import if removing it breaks other things? No, remove.
// Actually line 3 is: import { useTranslation } from 'react-i18next';
// I will just remove it.
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Tab,
    Tabs,
    Alert,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Chip,
    CardMedia,
    CardActions,
    Autocomplete
} from '@mui/material';
import {
    Send,
    Image as ImageIcon,
    Telegram,
    VideoLibrary,
    Delete,
    Edit,
    Schedule,
    CheckCircle,
    Error as ErrorIcon,
    Cancel,
    AccessTime
} from '@mui/icons-material';
import { addBot, getBots, sendMessage, sendPhoto, sendVideo, removeBot, selectBot, getUpdates } from '../redux/slices/telegramSlice';
import { createScheduledPost, getScheduledPosts, updateScheduledPost, deleteScheduledPost, postNow } from '../redux/slices/scheduledPostSlice';

// Chat Selector Component (Moved outside to prevent re-renders)
const ChatSelector = ({ value, onChange, savedChats = [], label = "اختر القناة / المحادثة" }) => {
    return (
        <Autocomplete
            freeSolo
            options={savedChats}
            filterOptions={(options, state) => {
                const inputValue = state.inputValue.toLowerCase();
                return options.filter(chat =>
                    chat.id.toLowerCase().includes(inputValue) ||
                    (chat.title && chat.title.toLowerCase().includes(inputValue)) ||
                    (chat.username && chat.username.toLowerCase().includes(inputValue))
                );
            }}
            getOptionLabel={(option) => {
                // If option is a string (manual input), return it
                if (typeof option === 'string') return option;
                // Otherwise return the ID (this is what shows in the text field after selection)
                return option.id;
            }}
            renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                // If manual input or unknown format
                if (typeof option === 'string') return <li key={key} {...otherProps}>{option}</li>;

                return (
                    <li key={key} {...otherProps}>
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="body1">
                                {option.title || option.id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {option.type} | {option.username ? `@${option.username}` : ''} | ID: {option.id}
                            </Typography>
                        </Box>
                    </li>
                );
            }}
            // Value prop expects the option object if it matches, or the string if not found
            value={savedChats.find(c => c.id === value) || value || null}

            onChange={(event, newValue) => {
                if (newValue && typeof newValue === 'object') {
                    // User selected a chat from list -> set ID
                    onChange(newValue.id);
                } else if (newValue) {
                    // User typed something and pressed enter (or freeSolo)
                    onChange(newValue);
                } else {
                    onChange('');
                }
            }}
            onInputChange={(event, newInputValue) => {
                // Only update if it's manual typing?
                // Actually relying on onChange for selection is better.
                // For freeSolo, onInputChange is needed.
                // But if I select, onInputChange fires with getOptionLabel -> ID.
                // So "12345". This is fine!
                onChange(newInputValue);
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    fullWidth
                    helperText={
                        savedChats.length === 0
                            ? "قم بفحص التحديثات لجلب القنوات تلقائياً (تأكد من إرسال رسالة للبوت أولاً)"
                            : `تم حفظ ${savedChats.length} قناة/محادثة. ابحث بالاسم أو ID.`
                    }
                />
            )}
            sx={{ mb: 2 }}
        />
    );
};

const TelegramManager = () => {
    const dispatch = useDispatch();
    const { bots, selectedBot, loading } = useSelector((state) => state.telegram);
    const { posts, loading: postsLoading } = useSelector((state) => state.scheduledPosts);
    const [currentTab, setCurrentTab] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Forms
    const [botToken, setBotToken] = useState('');
    const [messageForm, setMessageForm] = useState({ chatId: '', text: '' });
    const [photoForm, setPhotoForm] = useState({ chatId: '', caption: '' });
    const [videoForm, setVideoForm] = useState({ chatId: '', caption: '' });
    const [selectedFile, setSelectedFile] = useState(null);

    // Scheduled Post Form
    const [postDialog, setPostDialog] = useState({ open: false, mode: 'create', postId: null });
    const [postForm, setPostForm] = useState({ caption: '', scheduledTime: '', media: null, targetId: '', postType: 'text' });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, postId: null });
    const [botDeleteDialog, setBotDeleteDialog] = useState({ open: false, botId: null });
    const [updatesDialog, setUpdatesDialog] = useState(false); // New State
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        dispatch(getBots());
        dispatch(getScheduledPosts({ platform: 'telegram' }));
    }, [dispatch]);

    // Bot Management
    const handleAddBot = async () => {
        if (!botToken) {
            setSnackbar({ open: true, message: 'يرجى إدخال Bot Token', severity: 'error' });
            return;
        }

        try {
            await dispatch(addBot(botToken)).unwrap();
            setSnackbar({ open: true, message: 'تم إضافة البوت بنجاح!', severity: 'success' });
            setBotToken('');
        } catch (error) {
            setSnackbar({ open: true, message: error || 'فشل إضافة البوت', severity: 'error' });
        }
    };

    const handleDeleteBot = async () => {
        try {
            await dispatch(removeBot(botDeleteDialog.botId)).unwrap();
            setSnackbar({ open: true, message: 'تم حذف البوت بنجاح', severity: 'success' });
            setBotDeleteDialog({ open: false, botId: null });
        } catch (error) {
            setSnackbar({ open: true, message: error || 'فشل حذف البوت', severity: 'error' });
        }
    };

    // Immediate Logic
    const handleSendMessage = async () => {
        if (!selectedBot || !messageForm.chatId || !messageForm.text) {
            setSnackbar({ open: true, message: 'يرجى ملء جميع الحقول', severity: 'error' });
            return;
        }

        try {
            await dispatch(sendMessage({
                accountId: selectedBot,
                chatId: messageForm.chatId,
                text: messageForm.text
            })).unwrap();
            setSnackbar({ open: true, message: 'تم إرسال الرسالة!', severity: 'success' });
            setMessageForm({ chatId: '', text: '' });
        } catch (error) {
            setSnackbar({ open: true, message: error || 'فشل إرسال الرسالة', severity: 'error' });
        }
    };

    const handleSendPhoto = async () => {
        if (!selectedBot || !photoForm.chatId || !selectedFile) {
            setSnackbar({ open: true, message: 'يرجى اختيار صورة وإدخال Chat ID', severity: 'error' });
            return;
        }

        try {
            await dispatch(sendPhoto({
                accountId: selectedBot,
                chatId: photoForm.chatId,
                photo: selectedFile,
                caption: photoForm.caption
            })).unwrap();
            setSnackbar({ open: true, message: 'تم إرسال الصورة!', severity: 'success' });
            setPhotoForm({ chatId: '', caption: '' });
            setSelectedFile(null);
        } catch (error) {
            setSnackbar({ open: true, message: error || 'فشل إرسال الصورة', severity: 'error' });
        }
    };

    const handleSendVideo = async () => {
        if (!selectedBot || !videoForm.chatId || !selectedFile) {
            setSnackbar({ open: true, message: 'يرجى اختيار فيديو وإدخال Chat ID', severity: 'error' });
            return;
        }

        try {
            await dispatch(sendVideo({
                accountId: selectedBot,
                chatId: videoForm.chatId,
                video: selectedFile,
                caption: videoForm.caption
            })).unwrap();
            setSnackbar({ open: true, message: 'تم إرسال الفيديو!', severity: 'success' });
            setVideoForm({ chatId: '', caption: '' });
            setSelectedFile(null);
        } catch (error) {
            setSnackbar({ open: true, message: error || 'فشل إرسال الفيديو', severity: 'error' });
        }
    };

    // Scheduled Posts Logic
    const handleOpenPostDialog = (mode = 'create', post = null) => {
        if (mode === 'edit' && post) {
            setPostForm({
                caption: post.content.caption,
                scheduledTime: new Date(post.scheduledTime).toISOString().slice(0, 16),
                media: null,
                targetId: post.targetId || '',
                postType: post.postType
            });
            setPostDialog({ open: true, mode, postId: post._id });
        } else {
            setPostForm({ caption: '', scheduledTime: '', media: null, targetId: '', postType: 'text' });
            setPostDialog({ open: true, mode: 'create', postId: null });
        }
    };

    const handleSubmitPost = async () => {
        if (!selectedBot) {
            setSnackbar({ open: true, message: 'يرجى اختيار بوت أولاً', severity: 'error' });
            return;
        }
        if (!postForm.scheduledTime || !postForm.targetId) {
            setSnackbar({ open: true, message: 'يرجى تحديد وقت النشر و Chat ID', severity: 'error' });
            return;
        }

        try {
            const postData = {
                platform: 'telegram',
                accountId: selectedBot,
                targetId: postForm.targetId,
                caption: postForm.caption,
                scheduledTime: new Date(postForm.scheduledTime).toISOString(),
                postType: postForm.postType,
                media: postForm.media
            };

            if (postDialog.mode === 'create') {
                await dispatch(createScheduledPost(postData)).unwrap();
                setSnackbar({ open: true, message: 'تم جدولة المنشور بنجاح!', severity: 'success' });
            } else {
                await dispatch(updateScheduledPost({ id: postDialog.postId, updateData: postData })).unwrap();
                setSnackbar({ open: true, message: 'تم تحديث المنشور بنجاح!', severity: 'success' });
            }

            setPostDialog({ open: false, mode: 'create', postId: null });
            dispatch(getScheduledPosts({ platform: 'telegram' }));
        } catch (error) {
            setSnackbar({ open: true, message: error || 'فشل العملية', severity: 'error' });
        }
    };

    const handleDeletePost = async () => {
        try {
            await dispatch(deleteScheduledPost(deleteDialog.postId)).unwrap();
            setSnackbar({ open: true, message: 'تم حذف المنشور بنجاح!', severity: 'success' });
            setDeleteDialog({ open: false, postId: null });
            dispatch(getScheduledPosts({ platform: 'telegram' }));
        } catch (error) {
            setSnackbar({ open: true, message: error || 'فشل الحذف', severity: 'error' });
        }
    };

    const handlePostNow = async (postId) => {
        try {
            await dispatch(postNow(postId)).unwrap();
            setSnackbar({ open: true, message: 'تم نشر المنشور!', severity: 'success' });
            dispatch(getScheduledPosts({ platform: 'telegram' }));
        } catch (error) {
            setSnackbar({ open: true, message: error || 'فشل النشر', severity: 'error' });
        }
    };

    // Auto-fill Chat ID handler
    const handleSelectChatId = (id) => {
        const idStr = String(id);
        setMessageForm(prev => ({ ...prev, chatId: idStr }));
        setPhotoForm(prev => ({ ...prev, chatId: idStr }));
        setVideoForm(prev => ({ ...prev, chatId: idStr }));
        setPostForm(prev => ({ ...prev, targetId: idStr }));
        setUpdatesDialog(false);
        setSnackbar({ open: true, message: `تم تحديد ID: ${idStr} لجميع النماذج`, severity: 'success' });
    };

    const selectedBotData = bots.find(b => b._id === selectedBot);



    // Helpers
    const getStatusChip = (status) => {
        const statusConfig = {
            pending: { label: 'قيد الانتظار', color: 'warning', icon: <AccessTime /> },
            posted: { label: 'تم النشر', color: 'success', icon: <CheckCircle /> },
            failed: { label: 'فشل', color: 'error', icon: <ErrorIcon /> },
            cancelled: { label: 'ملغي', color: 'default', icon: <Cancel /> }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return <Chip label={config.label} color={config.color} size="small" icon={config.icon} />;
    };

    const filteredPosts = statusFilter === 'all' ? posts : posts.filter(p => p.status === statusFilter);
    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Header */}
            <Box sx={{
                background: 'linear-gradient(135deg, #2AABEE 0%, #229ED9 100%)',
                borderRadius: 3,
                p: { xs: 3, md: 4 },
                mb: 3,
                color: 'white'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Telegram sx={{ fontSize: { xs: 32, md: 40 } }} />
                        <Box>
                            <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                                إدارة Telegram
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>إدارة البوتات والقنوات</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {bots.length > 0 && (
                            <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                onClick={() => setUpdatesDialog(true)}
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                            >
                                🔍 فحص التحديثات (IDs)
                            </Button>
                        )}
                        {bots.length > 0 && (
                            <FormControl sx={{ minWidth: 250, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
                                <InputLabel sx={{ color: 'white' }}>البوت النشط</InputLabel>
                                <Select
                                    value={selectedBot || ''}
                                    onChange={(e) => dispatch(selectBot(e.target.value))}
                                    sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
                                >
                                    {bots.map((bot) => (
                                        <MenuItem key={bot._id} value={bot._id}>@ {bot.botUsername} - {bot.botName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                        <Tabs
                            value={currentTab}
                            onChange={(e, newValue) => setCurrentTab(newValue)}
                            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                        >
                            <Tab label="إضافة بوت" />
                            <Tab label="إرسال رسالة" disabled={!selectedBot} />
                            <Tab label="إرسال صورة" disabled={!selectedBot} />
                            <Tab label="إرسال فيديو" disabled={!selectedBot} />
                            <Tab label="🗓️ المنشورات المجدولة" disabled={!selectedBot} />
                        </Tabs>

                        <CardContent sx={{ p: 4 }}>
                            {/* TAB 0: Add Bot */}
                            {currentTab === 0 && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>إضافة بوت جديد</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>احصل على Token من @BotFather</Typography>
                                    <TextField
                                        fullWidth
                                        label="Bot Token"
                                        value={botToken}
                                        onChange={(e) => setBotToken(e.target.value)}
                                        sx={{ mb: 3 }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleAddBot}
                                        disabled={loading}
                                        sx={{ background: 'linear-gradient(135deg, #2AABEE 0%, #229ED9 100%)', px: 4, py: 1.5 }}
                                    >
                                        إضافة البوت
                                    </Button>

                                    {bots.length > 0 && (
                                        <Box sx={{ mt: 4 }}>
                                            <Typography variant="subtitle1" gutterBottom>البوتات المضافة ({bots.length})</Typography>
                                            {bots.map((bot) => (
                                                <Card key={bot._id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Box>
                                                        <Typography variant="body1" fontWeight="bold">@{bot.botUsername}</Typography>
                                                        <Typography variant="body2" color="text.secondary">{bot.botName}</Typography>
                                                    </Box>
                                                    <IconButton color="error" onClick={() => setBotDeleteDialog({ open: true, botId: bot._id })}>
                                                        <Delete />
                                                    </IconButton>
                                                </Card>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* TAB 1: Send Message */}
                            {currentTab === 1 && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>إرسال رسالة نصية</Typography>
                                    <Alert severity="info" sx={{ mb: 3 }}>البوت: @{selectedBotData?.botUsername}</Alert>
                                    <ChatSelector
                                        value={messageForm.chatId}
                                        onChange={(val) => setMessageForm({ ...messageForm, chatId: val })}
                                        savedChats={selectedBotData?.savedChats || []}
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="الرسالة"
                                        value={messageForm.text}
                                        onChange={(e) => setMessageForm({ ...messageForm, text: e.target.value })}
                                        sx={{ mb: 3 }}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<Send />}
                                        onClick={handleSendMessage}
                                        sx={{ background: 'linear-gradient(135deg, #2AABEE 0%, #229ED9 100%)' }}
                                    >
                                        إرسال
                                    </Button>
                                </Box>
                            )}

                            {/* TAB 2: Send Photo */}
                            {currentTab === 2 && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>إرسال صورة</Typography>
                                    <ChatSelector
                                        value={photoForm.chatId}
                                        onChange={(val) => setPhotoForm({ ...photoForm, chatId: val })}
                                        savedChats={selectedBotData?.savedChats || []}
                                    />
                                    <TextField
                                        fullWidth
                                        label="التعليق"
                                        value={photoForm.caption}
                                        onChange={(e) => setPhotoForm({ ...photoForm, caption: e.target.value })}
                                        sx={{ mb: 2 }}
                                    />
                                    <Button variant="outlined" component="label" startIcon={<ImageIcon />} sx={{ mb: 3 }}>
                                        اختر صورة
                                        <input type="file" hidden accept="image/*" onChange={(e) => setSelectedFile(e.target.files[0])} />
                                    </Button>
                                    {selectedFile && <Typography variant="body2" sx={{ mb: 2 }}>✅ {selectedFile.name}</Typography>}
                                    <Button
                                        variant="contained"
                                        startIcon={<Send />}
                                        onClick={handleSendPhoto}
                                        sx={{ background: 'linear-gradient(135deg, #2AABEE 0%, #229ED9 100%)' }}
                                    >
                                        إرسال
                                    </Button>
                                </Box>
                            )}

                            {/* TAB 3: Send Video */}
                            {currentTab === 3 && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>إرسال فيديو</Typography>
                                    <ChatSelector
                                        value={videoForm.chatId}
                                        onChange={(val) => setVideoForm({ ...videoForm, chatId: val })}
                                        savedChats={selectedBotData?.savedChats || []}
                                    />
                                    <TextField
                                        fullWidth
                                        label="التعليق"
                                        value={videoForm.caption}
                                        onChange={(e) => setVideoForm({ ...videoForm, caption: e.target.value })}
                                        sx={{ mb: 2 }}
                                    />
                                    <Button variant="outlined" component="label" startIcon={<VideoLibrary />} sx={{ mb: 3 }}>
                                        اختر فيديو
                                        <input type="file" hidden accept="video/*" onChange={(e) => setSelectedFile(e.target.files[0])} />
                                    </Button>
                                    {selectedFile && <Typography variant="body2" sx={{ mb: 2 }}>✅ {selectedFile.name}</Typography>}
                                    <Button
                                        variant="contained"
                                        startIcon={<Send />}
                                        onClick={handleSendVideo}
                                        sx={{ background: 'linear-gradient(135deg, #2AABEE 0%, #229ED9 100%)' }}
                                    >
                                        إرسال
                                    </Button>
                                </Box>
                            )}

                            {/* TAB 4: Scheduled Posts */}
                            {currentTab === 4 && (
                                <Box>
                                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                        <Typography variant="h6">المنشورات المجدولة</Typography>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                                <InputLabel>الحالة</InputLabel>
                                                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="الحالة">
                                                    <MenuItem value="all">الكل</MenuItem>
                                                    <MenuItem value="pending">قيد الانتظار</MenuItem>
                                                    <MenuItem value="posted">تم النشر</MenuItem>
                                                    <MenuItem value="failed">فشل</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <Button
                                                variant="contained"
                                                startIcon={<Schedule />}
                                                onClick={() => handleOpenPostDialog('create')}
                                                sx={{ background: 'linear-gradient(135deg, #2AABEE 0%, #229ED9 100%)' }}
                                            >
                                                جدولة منشور
                                            </Button>
                                        </Box>
                                    </Box>

                                    <Grid container spacing={2}>
                                        {filteredPosts.length === 0 ? (
                                            <Grid item xs={12}>
                                                <Alert severity="info">لا توجد منشورات مجدولة</Alert>
                                            </Grid>
                                        ) : (
                                            filteredPosts.map((post) => (
                                                <Grid item xs={12} sm={6} md={4} key={post._id}>
                                                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                        {post.content.mediaPath && (
                                                            <CardMedia component="div" sx={{ height: 160, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                {post.postType === 'video' ? <VideoLibrary /> : <ImageIcon />}
                                                            </CardMedia>
                                                        )}
                                                        <CardContent sx={{ flexGrow: 1 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#2AABEE' }}>
                                                                إلى: {post.targetId}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxHeight: 60, overflow: 'hidden' }}>
                                                                {post.content.caption || '(بدون نص)'}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                                                <Chip label={new Date(post.scheduledTime).toLocaleString()} size="small" icon={<AccessTime />} />
                                                                {getStatusChip(post.status)}
                                                            </Box>
                                                        </CardContent>
                                                        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                                                            <Box>
                                                                {post.status === 'pending' && (
                                                                    <IconButton size="small" color="primary" onClick={() => handleOpenPostDialog('edit', post)}>
                                                                        <Edit />
                                                                    </IconButton>
                                                                )}
                                                                <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, postId: post._id })}>
                                                                    <Delete />
                                                                </IconButton>
                                                            </Box>
                                                            {post.status === 'pending' && (
                                                                <Button size="small" variant="outlined" onClick={() => handlePostNow(post._id)}>نشر</Button>
                                                            )}
                                                        </CardActions>
                                                    </Card>
                                                </Grid>
                                            ))
                                        )}
                                    </Grid>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Post Dialog */}
            <Dialog open={postDialog.open} onClose={() => setPostDialog({ ...postDialog, open: false })} maxWidth="sm" fullWidth>
                <DialogTitle>{postDialog.mode === 'create' ? 'جدولة منشور جديد' : 'تعديل المنشور'}</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>البوت: @{selectedBotData?.botUsername}</Alert>

                    <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                        <InputLabel>نوع المنشور</InputLabel>
                        <Select
                            value={postForm.postType}
                            onChange={(e) => setPostForm({ ...postForm, postType: e.target.value })}
                            label="نوع المنشور"
                        >
                            <MenuItem value="text">نص فقط</MenuItem>
                            <MenuItem value="image">صورة</MenuItem>
                            <MenuItem value="video">فيديو</MenuItem>
                        </Select>
                    </FormControl>

                    <ChatSelector
                        value={postForm.targetId}
                        onChange={(val) => setPostForm({ ...postForm, targetId: val })}
                        label="Chat ID / أسم القناة"
                        savedChats={selectedBotData?.savedChats || []}
                    />

                    <TextField
                        fullWidth
                        label="النص / التعليق"
                        multiline
                        rows={4}
                        value={postForm.caption}
                        onChange={(e) => setPostForm({ ...postForm, caption: e.target.value })}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        type="datetime-local"
                        label="وقت النشر"
                        value={postForm.scheduledTime}
                        onChange={(e) => setPostForm({ ...postForm, scheduledTime: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 2 }}
                    />

                    {postForm.postType !== 'text' && (
                        <Button variant="outlined" component="label" fullWidth sx={{ mb: 2 }}>
                            {postForm.media ? `✅ تم التحديد: ${postForm.media.name}` : `اختر ${postForm.postType === 'image' ? 'صورة' : 'فيديو'}`}
                            <input
                                type="file"
                                hidden
                                accept={postForm.postType === 'image' ? "image/*" : "video/*"}
                                onChange={(e) => setPostForm({ ...postForm, media: e.target.files[0] })}
                            />
                        </Button>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPostDialog({ ...postDialog, open: false })}>إلغاء</Button>
                    <Button variant="contained" onClick={handleSubmitPost} disabled={postsLoading}>
                        {postDialog.mode === 'create' ? 'جدولة' : 'تحديث'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Post Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, postId: null })}>
                <DialogTitle>تأكيد الحذف</DialogTitle>
                <DialogContent>
                    <Typography>هل أنت متأكد من حذف هذا المنشور المجدول؟</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, postId: null })}>إلغاء</Button>
                    <Button color="error" variant="contained" onClick={handleDeletePost}>حذف</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Bot Dialog */}
            <Dialog open={botDeleteDialog.open} onClose={() => setBotDeleteDialog({ open: false, botId: null })}>
                <DialogTitle>تأكيد حذف البوت</DialogTitle>
                <DialogContent>
                    <Typography>هل أنت متأكد من حذف هذا البوت؟ سيتم فقدان الوصول إليه.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBotDeleteDialog({ open: false, botId: null })}>إلغاء</Button>
                    <Button color="error" variant="contained" onClick={handleDeleteBot}>حذف البوت</Button>
                </DialogActions>
            </Dialog>

            {/* Updates Dialog */}
            <UpdatesDialog
                open={updatesDialog}
                onClose={() => setUpdatesDialog(false)}
                botId={selectedBot}
                onSelectId={handleSelectChatId}
            />

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </Box >
    );
};

// Sub-component for Updates Dialog
const UpdatesDialog = ({ open, onClose, botId, onSelectId }) => {
    const dispatch = useDispatch();
    const [updates, setUpdates] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && botId) {
            fetchUpdates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, botId]);

    const fetchUpdates = async () => {
        setLoading(true);
        try {
            const result = await dispatch(getUpdates(botId)).unwrap();
            setUpdates(result);
            // Refresh bots to get updated knownChats
            dispatch(getBots());
        } catch (error) {
            console.error('Failed to fetch updates:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                آخر المحادثات (للحصول على Chat ID)
                <Button size="small" onClick={fetchUpdates} sx={{ ml: 2 }}>تحديث</Button>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Typography>جاري التحميل...</Typography>
                ) : updates.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography color="text.secondary">لا توجد تحديثات جديدة.</Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            أرسل رسالة للبوت من حسابك أو القناة ثم اضغط تحديث ليقوم البوت بحفظ القناة تلقائياً.
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>انسخ الـ ID أو اضغط "استخدام" لملء النماذج تلقائياً</Alert>
                        {updates.map((update) => (
                            <Card key={update.update_id} variant="outlined" sx={{ mb: 1, p: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {update.chat.title} ({update.chat.type})
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {update.text || '(media/other)'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip
                                            label={update.chat.id}
                                            color="default"
                                            variant="outlined"
                                            onClick={() => {
                                                navigator.clipboard.writeText(update.chat.id);
                                                alert('تم نسخ ID: ' + update.chat.id);
                                            }}
                                        />
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => onSelectId(update.chat.id)}
                                        >
                                            استخدام
                                        </Button>
                                    </Box>
                                </Box>
                            </Card>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>إغلاق</Button>
            </DialogActions>
        </Dialog >
    );
};

export default TelegramManager;
